import React, { useEffect, useState } from 'react';
import {
  Button,
  HStack,
  LoadingSpinner,
  RadioListItem,
  Text,
  VStack,
} from '@adc/parallax-component-library';
import MessageBanner from '../messageBanner/MessageBanner';
import { Dialog } from 'tamagui';
import i18n from '../../utilities/i18n';
import { useAppSelector } from '../../reducers/hooks';
import { store } from '../../reducers';
import { changeCurrentOrganization } from '../../reducers/organization/reducer';
import { useQuery } from '@tanstack/react-query';
import { organizationsQueryOptions } from '../../queryOptions';
import { setSessionObject } from '../../utilities/storage';

type Props = {
  open: boolean;
  onClose: () => void;
};

export function ChangeOrganizationModal({ open, onClose }: Props) {
  const organizations = useQuery(organizationsQueryOptions());

  const { currentOrgKey } = useAppSelector((state) => state.organizations);

  const [newCurrentOrganization, setNewCurrentOrganization] = useState('');

  useEffect(() => {
    setNewCurrentOrganization(currentOrgKey);
  }, [currentOrgKey]);

  const handleOrganizationChange = () => {
    store.dispatch(changeCurrentOrganization(newCurrentOrganization));
    setSessionObject('currentOrganization', newCurrentOrganization);
    onClose();
  };

  return (
    <Dialog open={open}>
      <Dialog.Portal>
        <Dialog.Overlay key="overlay" />
        <Dialog.Content key="content">
          <HStack justifyContent="space-between">
            <Dialog.Title>
              <Text type="$h3.strong">
                {i18n.t('UserMenu.changeOrganization')}
              </Text>
            </Dialog.Title>
          </HStack>

          <VStack gap="$2" paddingVertical="$4">
            {organizations.isPending ? (
              <VStack alignItems="center" justifyContent="center" flexGrow={1}>
                <LoadingSpinner testID="change-organizations-loading-spinner" />
              </VStack>
            ) : null}

            {organizations.isError ? (
              <MessageBanner
                type="danger"
                headerText={i18n.t('Page_Organizations.error_title')}
                bodyText={i18n.t('Page_Organizations.error_default')}
                testID="change-organizations-error-message"
              />
            ) : null}

            {organizations.isSuccess
              ? organizations.data.map(({ key, name, description }) => (
                  <RadioListItem
                    key={key}
                    title={name}
                    selected={newCurrentOrganization === key}
                    onPress={() => setNewCurrentOrganization(key)}
                    disabled={false}
                    label={description}
                    testID={`organization-${key}`}
                  />
                ))
              : null}
          </VStack>
          <HStack justifyContent="flex-end" gap="$2">
            <Button
              variant="secondary"
              label={i18n.t('Common.cancel')}
              onPress={onClose}
              testID="change-organization-cancel-button"
            />
            <Button
              variant="primary"
              label={i18n.t('Common.select')}
              onPress={handleOrganizationChange}
              testID="change-organization-select-button"
            />
          </HStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
